import type { AnalyticsInterface } from '@core/analytics/analytics';
import {
  maybeAddSegmentContext,
  getSegmentIntegrations,
  getConsentIDs,
} from '@core/analytics/analytics';
import { toDeviceCategoryFromWindow } from '@core/analytics/deviceCategory';
import { toAnalyticsKeysWithOverrides } from '@core/analytics/formatters';
import * as Segment from '@core/analytics/segment';
import type { EventType, Identity, Load } from '@core/analytics/types';

const segmentHasIdentity = (): Promise<boolean> =>
  new Promise(resolve =>
    (<any>window).analytics?.ready(() => {
      const user = (<any>window).analytics.user();
      resolve(Boolean(user && user.id()));
    }),
  );

export const Analytics = {
  Make: (
    track: typeof Segment.trackSegment,
    hasIdentity: typeof segmentHasIdentity,
  ): AnalyticsInterface => {
    class Internal {
      track<T extends object>(mixedEvent: EventType<T>): void {
        if (typeof mixedEvent === 'string') {
          console.warn(`Unhandled track event ${mixedEvent}`);
        } else {
          const { event, ...properties } = mixedEvent;
          const consentProperties = getConsentIDs(properties);
          hasIdentity().then(identity => {
            if (identity) {
              track({
                event,
                options: {
                  ...maybeAddSegmentContext(properties.context),
                  ...getSegmentIntegrations(),
                },
                properties: toAnalyticsKeysWithOverrides({
                  ...consentProperties,
                  platformType: 'Web',
                  formFactor: toDeviceCategoryFromWindow(),
                }),
              });
            }
          });
        }
      }

      group() {
        // no op - this is not used
      }

      identify<T extends object>(identity: Identity<T>) {
        const properties = getConsentIDs(identity.properties);
        const payload = {
          ...identity,
          properties,
          options: {
            ...maybeAddSegmentContext(),
            ...getSegmentIntegrations(),
          },
        };
        Segment.identifySegment(payload);
      }

      reset(): void {
        Segment.resetSegment();
      }

      load(load: Load, options?: Segment.SegmentLoadOptions): void {
        Segment.loadSegment(load, options);
      }

      page<T extends object>(page: Segment.SegmentPage<T>): void {
        const properties = getConsentIDs(page.properties);
        const payload = {
          ...page,
          properties,
          options: {
            ...maybeAddSegmentContext(),
            ...getSegmentIntegrations(),
          },
        };
        Segment.pageSegment(payload);
      }

      ready(callback: () => void): void {
        Segment.readySegment(callback);
      }

      anonymousId(callback: (anonymousId: string) => void): void {
        Segment.anonymousIdSegment(callback);
      }
    }
    return new Internal();
  },
};

export const makeAnalytics = (
  track = Segment.trackSegment,
  hasIdentity = segmentHasIdentity,
) => Analytics.Make(track, hasIdentity);
